import Header from '../../Header/Header';
import { Icon, Loader } from '@ohif/ui';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, ErrorMessage, Field } from 'formik';
import { SuccessModal } from '@ohif/ui';
import * as Yup from 'yup';
import { AppDispatch, RootState } from '@custom/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { addActiveMachine } from '@custom/rtk-apis/activeMachine/addActivemachine';
import { customStyle } from '@custom/Utils/utils';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls } from '@custom/Utils/constants/apiUrls';
import { ConfirmationPopup } from '@ohif/ui';
import { useTheme } from "../../../ui/src/contextProviders";
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import { addCaseCredit } from '@custom/rtk-apis/Setting/creditForCaseToCase/addCaseCredit';
import { servicesManager } from "../../../app/src/App";
import { _routes } from '@custom/Utils/constants/routes';
import { updateCaseCredit } from '@custom/rtk-apis/Setting/creditForCaseToCase/updateCaseCredit';
import { clearAddCaseCreditState } from '@custom/store/slices/Setting/creditForCaseToCase/addCaseCreditSlice';
import { clearUpdateCaseCreditState } from '@custom/store/slices/Setting/creditForCaseToCase/updateCaseCreditSlice';
import { getCaseToCaseHospital } from '@custom/rtk-apis/Setting/creditForCaseToCase/getCaseToCaseHospital';

const validationSchema = Yup.object().shape({
  modality: Yup.string().required('Modality is required'),
  credit: Yup.number()
    .required('Credits is required')
    .positive('Credits must be a positive number')
    .integer('Credits must be an integer'),
});

const AddEditCaseToCase = () => {
  const isDarkMode = useTheme();
  const dispatch: AppDispatch = useDispatch();
  const { uiNotificationService } = servicesManager?.services;

  const modalityStudyResponse = useSelector(
    (state: RootState) => state?.getModalityStudySliceResponse
  );
  const addCaseCreditSliceResponse = useSelector(
    (state: RootState) => state?.addCaseCreditResponse
  );
  const updateCaseCreditSliceResponse = useSelector(
    (state: RootState) => state?.updateCaseCreditResponse
  );
  const getCaseToCaseHospitalResponse = useSelector(
    (state: RootState) => state.getCaseToCaseHospitalResponse
  );

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [commonData, setCommonData] = useState({ modality: [] });
  const [modalityData, setModalityData] = useState([]);
  const [edit, setIsEdit] = useState(false);
  const [modal, setModal] = useState(false);
  const [exceptionHospitals, setExceptionHospitals] = useState([]);
  const [initialValues, setInitialValues] = useState({
    _id: "",
    modality: '',
    credit: '',
    exceptionHospitals: [],
  });
  const [formData, setFormData] = useState();
  const [hospitalData, setHospitalData] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const CreditsForCaseToCase = location.state?.CreditsForCaseToCase;

  useEffect(() => {
    if (modalityStudyResponse?.status === 'SUCCESS') {
      const modalities = modalityStudyResponse?.result?.modalities;
      let uniqueModalities = Array.from(new Set(modalities));

      if (!uniqueModalities.includes('Histopathology')) {
        uniqueModalities = [...uniqueModalities, 'Histopathology'];
      }

      const modalityList = uniqueModalities?.map(modality => ({
        label: modality,
        value: modality
      }));
      setModalityData(modalityList);
    }
  }, [modalityStudyResponse?.status]);

  useEffect(() => {
    if (CreditsForCaseToCase) {
      setIsEdit(true);
      setInitialValues(prev => ({
        ...prev,
        _id: CreditsForCaseToCase._id,
        modality: CreditsForCaseToCase.modality,
        credit: CreditsForCaseToCase.credit,
      }));
    } else {
      setIsEdit(false);
    }
  }, [CreditsForCaseToCase]);

  useEffect(() => {
    dispatch(getModalityStudy());
    dispatch(getCaseToCaseHospital({ _id: CreditsForCaseToCase?._id }));
  }, []);

  useEffect(() => {
    if (getCaseToCaseHospitalResponse?.status === 'SUCCESS' && getCaseToCaseHospitalResponse?.data?.length > 0) {
      const updatedHospitals = getCaseToCaseHospitalResponse.data.map(hospital => ({
        id: hospital._id,
        name: hospital.name,
        credit: hospital.matchedCaseCredit?.credit || 0,
        useNewTariff: false,
        newTariff: null,
      }));
      setInitialValues(prev => ({
        ...prev,
        _id: CreditsForCaseToCase?._id || "",
        modality: CreditsForCaseToCase?.modality || '',
        credit: CreditsForCaseToCase?.credit || '',
        exceptionHospitals: updatedHospitals
      }));
      const data = getCaseToCaseHospitalResponse?.data?.map((hospital: any) => ({
        id: hospital?._id || '',
        name: hospital?.name || '',
        credit: hospital?.matchedTeleCredit?.credit || 0,
      }));
      setHospitalData(getCaseToCaseHospitalResponse.data);
      setExceptionHospitals(data);
    }
  }, [getCaseToCaseHospitalResponse?.status, getCaseToCaseHospitalResponse?.data]);

  useEffect(() => {
    if (addCaseCreditSliceResponse?.status === 'SUCCESS') {
      setModal(true);
      setTimeout(() => {
        setModal(false);
        dispatch(clearAddCaseCreditState());
        navigate(_routes.settings.pricing);
      }, 2000);
    } else if (addCaseCreditSliceResponse?.status === 'FAILED') {
      dispatch(clearAddCaseCreditState());
      uiNotificationService.show({
        title: 'Modality Credit',
        message: addCaseCreditSliceResponse?.error || 'Something went wrong...',
        type: 'error',
      });
    }
    if (updateCaseCreditSliceResponse?.status === 'SUCCESS') {
      setModal(true);
      setTimeout(() => {
        setModal(false);
        dispatch(clearUpdateCaseCreditState());
        navigate(_routes.settings.pricing);
      }, 2000);
    } else if (updateCaseCreditSliceResponse?.status === 'FAILED') {
      dispatch(clearUpdateCaseCreditState());
      uiNotificationService.show({
        title: 'Modality Credit',
        message: updateCaseCreditSliceResponse?.error || 'Something went wrong...',
        type: 'error',
      });
    }
  }, [addCaseCreditSliceResponse?.status, updateCaseCreditSliceResponse?.status]);

  const handleSaveForm = async (values, { resetForm }) => {
    if (edit) {
      setFormData(values);
      setIsPopupOpen(true);
    } else {
      handleDispatch(values);
    }
  };

  const handleDispatch = (formValues) => {
    if (edit) {
      dispatch(updateCaseCredit({ 
        credit: formValues?.credit, 
        id: initialValues._id,
        exceptionHospitals: formValues?.exceptionHospitals?.map(hospital => ({
          _id: hospital.id,
          credit: hospital.useNewTariff ? (hospital.newTariff || hospital.credit) : hospital.credit,
          useNewTariff: hospital.useNewTariff,
        })),
      }));
    } else {
      dispatch(addCaseCredit(formValues));
    }
  };

  const handleConfirm = async () => {
    setIsPopupOpen(false);
    handleDispatch(formData);
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const cancel = () => {
    navigate(-1);
  };

  const fetchCommonData = async () => {
    try {
      const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' });
      setCommonData(commonData?.result);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchCommonData();
  }, []);

  return (
    <div className="page page__full" style={
      isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
    }>
      <Header title="Plan Settings" isBack={true} />
      <div className="page__body">
        <div className="btn__group custom__plan__btn" style={{
          justifyContent: "flex-end",
          alignItems: "center",
        }}>
          <button
            className='btn btn__white'
            onClick={() => {
              navigate(_routes.settings.pricing);
            }}
          >
            Preview credits plan
          </button>
        </div>
        <div className='details'>
          <div className='form form--innersec'>
            <section className='form__section'>
              <h1 className='form__section__title'>Credits For Case-To-Case Setting</h1>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSaveForm}
                enableReinitialize={true}
              >
                {({ errors, values, handleChange, handleBlur, setFieldValue }) => {
                  return (
                  <Form>
                    <div className='formRow formRow__4'>
                      <div className='formCol'>
                        <div className='form__Field'>
                          <label className='form__label' htmlFor='modality'>
                            Modality
                            <span className='required-asterisk'>*</span>
                          </label>
                          <select
                            className='form__select form__select--sm'
                            id='modality'
                            name='modality'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={edit}
                            value={values.modality}
                            style={{ ...customStyle.select }}
                          >
                            <option value='' label='Select Modality' />
                            {modalityData?.map((modality, index) => (
                              <option key={index} value={modality.value}>
                                {modality.value}
                              </option>
                            ))}
                          </select>
                          <ErrorMessage name='modality' component='div' className='error text-error' />
                        </div>
                      </div>
                      <div className='formCol'>
                        <div className='form__Field'>
                          <label className='form__label' htmlFor='credits'>
                            Credits<span className='required-asterisk'>*</span>
                          </label>
                          <input
                            className='form__input form__input--sm'
                            type='number'
                            min={0}
                            id='credit'
                            name='credit'
                            placeholder='Credits'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.credit}
                            autoComplete='off'
                          />
                          <ErrorMessage name='credit' component='div' className='error text-error' />
                        </div>
                      </div>
                    </div>

                    {edit && getCaseToCaseHospitalResponse?.status === 'SUCCESS' && getCaseToCaseHospitalResponse?.data?.length > 0 ? (
                      <div className='exception-hospitals'>
                        <label
                          className="form__label"
                          style={{
                            display: 'flex',
                            fontWeight: 600,
                            fontSize: "1rem",
                            marginTop: "1.5rem",
                            marginBottom: "1.5rem",
                            alignItems: 'center',
                          }}
                        >
                          Exception Hospitals <Icon
                            className="cursor-pointer"
                            name="info"
                            style={{
                              width: "1rem",
                              height: "1rem",
                              marginLeft: '0.5rem',
                            }}
                          ></Icon>
                        </label>
                        <div className='mb-[12px] flex flex-wrap gap-8 sm:flex-col sm:gap-3'>
                          {values.exceptionHospitals.map((hospital, index) => (
                            <div
                              className="formCol"
                              key={hospital.id}
                              style={{ maxWidth: '400px' }}
                            >
                              <div
                                className="form__Field"
                                style={{
                                  padding: '0.75rem',
                                  backgroundColor: isDarkMode?.isDarkMode ? '#090c29' : '#f6ffff',
                                  border: `2px solid ${isDarkMode?.isDarkMode ? '#FFFFFF3D' : 'rgba(15, 132, 145, 0.24)'}`,
                                  borderRadius: '6px',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: isDarkMode?.isDarkMode ? '#E2E8F0' : '#1A202C',
                                    marginBottom: '0.5rem',
                                  }}
                                >
                                  {hospital.name} (Credit: {hospital.credit})
                                </div>
                                <div className="form__radio">
                                  <label className="flex items-center mr-8 custom-radio-container">
                                    <Field
                                      type="radio"
                                      name={`exceptionHospitals[${index}].useNewTariff`}
                                      value="true"
                                      checked={hospital.useNewTariff === true}
                                      onChange={() => {
                                        setFieldValue(`exceptionHospitals[${index}].useNewTariff`, true);
                                      }}
                                      className="form__radio__input"
                                    />
                                    <span className="form__radio__label">New Tariff</span>
                                  </label>
                                  <label className="flex items-center mr-8 custom-radio-container">
                                    <Field
                                      type="radio"
                                      name={`exceptionHospitals[${index}].useNewTariff`}
                                      value="false"
                                      checked={hospital.useNewTariff === false}
                                      onChange={() => {
                                        setFieldValue(`exceptionHospitals[${index}].useNewTariff`, false);
                                        setFieldValue(`exceptionHospitals[${index}].newTariff`, null);
                                      }}
                                      className="form__radio__input"
                                    />
                                    <span className="form__radio__label">Old Tariff</span>
                                  </label>
                                </div>
                                {hospital.useNewTariff && (
                                  <div className="form__Field" style={{ marginTop: '0.5rem' }}>
                                    <label className="form__label" htmlFor={`exceptionHospitals[${index}].newTariff`}>
                                      New Tariff<span className="required-asterisk">*</span>
                                    </label>
                                    <Field
                                      className="form__input form__input--sm"
                                      type="number"
                                      id={`exceptionHospitals[${index}].newTariff`}
                                      name={`exceptionHospitals[${index}].newTariff`}
                                      placeholder="New Tariff"
                                      autoComplete="off"
                                    />
                                    <ErrorMessage
                                      name={`exceptionHospitals[${index}].newTariff`}
                                      component="div"
                                      className="error text-error"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : edit ? (
                      <div className="formCol">
                        <div className="form__Field" style={{ marginBottom: '1.5rem' }}>
                          <label
                            className="form__label"
                            style={{
                              display: 'flex',
                              fontWeight: 600,
                              fontSize: "1rem",
                              marginTop: "1.5rem",
                              marginBottom: "1.5rem",
                              alignItems: 'center',

                            }}
                          >
                            Exception Hospitals <Icon
                              className="cursor-pointer"
                              name="info"
                              style={{
                                width: "1rem",
                                height: "1rem",
                                marginLeft: '0.5rem',
                              }}
                            ></Icon>
                          </label>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              backgroundColor: isDarkMode?.isDarkMode ? '#090c29' : '#F6FFFF',
                              border: `1px solid ${isDarkMode?.isDarkMode ? '#2D3748' : '#E2E8F0'}`,
                              borderRadius: '8px',
                              padding: '1rem',
                              gap: '0.25rem',
                            }}
                          >
                            <h3
                              style={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                margin: 0,
                                color: isDarkMode?.isDarkMode ? '#E2E8F0' : '#1A202C',
                              }}
                            >
                              No Exception Hospitals
                            </h3>
                            <p
                              style={{
                                fontSize: '0.875rem',
                                color: isDarkMode?.isDarkMode ? '#A0AEC0' : '#4A5568',
                                margin: 0,
                                lineHeight: 1.5,
                              }}
                            >
                              The common credit value applies to all hospitals for this modality, as no hospital-specific credits have been set.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className='btn__group'>
                      <button className='btn btn__primary' type='submit'>
                        {edit ? 'Update' : 'Save'}
                      </button>
                      <button className='btn btn__white' onClick={cancel}>
                        Cancel
                      </button>
                    </div>
                  </Form>
                )}}
              </Formik>
            </section>
          </div>
        </div>
      </div>
      <Loader isLoaderOpen={addCaseCreditSliceResponse?.loading || updateCaseCreditSliceResponse?.loading} />
      <ConfirmationPopup
        title={"Update Details!"}
        isOpen={isPopupOpen}
        message={`Are you sure you want update the changes? `}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <SuccessModal
        isSuccessModalOpen={modal}
        title={edit ? "Credit updated successfully." : "Credit saved successfully."}
        handleCloseModal={() => setModal(false)}
      />
    </div>
  );
};

export default AddEditCaseToCase;
